  import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Tabs,
  Tab,
  Container,
  Chip,
  Avatar,
  CardActionArea,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Market Trends articles
const marketTrendsArticles = [
  {
    id: 'market-trends-1',
    title: "The Smart Landlord's Guide: Why Property Management is a Game-Changer",
    description: "Discover how professional property management can transform your rental investment from a full-time headache into truly passive income. Learn the six key benefits that are making smart landlords switch to professional management.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1073&q=80",
    author: "Lantena Team",
    readTime: "5 min read",
    date: "March 25, 2025",
    category: "Market Trends"
  },
  {
    id: 'market-trends-2',
    title: "Mastering the Art of a Deal in Property and Project Management",
    description: "Learn the insider strategies that separate successful property investments from financial disasters. Discover how expert management techniques can protect your real estate portfolio from common pitfalls and maximize returns.",
    image: "https://images.unsplash.com/photo-1460317442991-0ec209397118?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
    author: "Lantena Team",
    readTime: "7 min read",
    date: "March 20, 2025",
    category: "Market Trends"
  }
];

// Investment articles
const investmentArticles = [
  {
    id: 'investment-1',
    title: "Investment Strategies: Smart Homes & Projects for Diaspora Investors",
    description: "Discover how Kenyans abroad can leverage smart real estate investments, luxury renovations, and technology-driven property management to build wealth and create a lasting legacy in Kenya's growing property market.",
    image: "https://images.unsplash.com/photo-1626178793926-22b28830aa30?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    author: "Lantena Team",
    readTime: "8 min read",
    date: "March 15, 2025",
    category: "Investment"
  }
];

// Property Management articles
const propertyManagementArticles = [
  {
    id: 'property-management-1',
    title: "The Future of Property Management: Why Smart Landlords Are Switching to Lantena",
    description: "Discover how the property management industry is transforming with technology and automation. Learn why forward-thinking landlords are embracing digital solutions for better returns and stress-free ownership.",
    image: "https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1073&q=80",
    author: "Lantena Team",
    readTime: "6 min read",
    date: "March 15, 2025",
    category: "Property Management"
  }
];

// Combine all articles
const allArticles = [...marketTrendsArticles, ...investmentArticles, ...propertyManagementArticles];

const CommunityBlog = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [filteredArticles, setFilteredArticles] = useState(allArticles);
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredArticles(allArticles);
    } else if (activeTab === 'market-trends') {
      setFilteredArticles(marketTrendsArticles);
    } else if (activeTab === 'investment') {
      setFilteredArticles(investmentArticles);
    } else if (activeTab === 'property-management') {
      setFilteredArticles(propertyManagementArticles);
    }
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get the icon for a category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Market Trends':
        return <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'Property Management':
        return <HomeWorkIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'Investment':
        return <AccountBalanceIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default:
        return <EmojiObjectsIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }
  };

  return (
    <Box sx={{ 
      backgroundColor: '#fbfbfb', 
      minHeight: '100vh',
      pt: { xs: 10, md: 12 },
      pb: 8
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#0a233b',
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              mb: 2
            }}
          >
            Community Blog
          </Typography>
        </Box>

        {/* Header Section */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#6a6565', 
              mt: 2,
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Stay informed with the latest insights, trends, and expert advice in real estate and property management.
          </Typography>
        </Box>

        {/* Tabs for Categories with FAQ button on the right */}
        <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="article categories"
            sx={{
              '& .MuiTab-root': { 
                color: '#6a6565',
                '&.Mui-selected': { color: '#f66b10' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#f66b10' },
            }}
          >
            <Tab label="All Articles" value="all" />
            <Tab label="Market Trends" value="market-trends" />
            <Tab label="Investment" value="investment" />
            <Tab label="Property Management" value="property-management" />
          </Tabs>
          
          <Button
            variant="contained"
            startIcon={<HelpOutlineIcon />}
            onClick={() => navigate('/faq')}
            sx={{
              bgcolor: '#f66b10',
              '&:hover': { bgcolor: '#e05a00' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              ml: 2,
            }}
          >
            View FAQ
          </Button>
        </Box>

        {/* Articles Grid */}
        <Grid container spacing={3}>
          {filteredArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 8,
                  },
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <CardActionArea 
                  component={Link} 
                  to={`/article/${article.id}`}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={article.image}
                    alt={article.title}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Chip
                      icon={getCategoryIcon(article.category)}
                      label={article.category}
                      size="small"
                      sx={{ 
                        mb: 2, 
                        backgroundColor: article.category === 'Market Trends' ? 'rgba(166, 209, 234, 0.2)' : article.category === 'Investment' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(246, 107, 16, 0.1)',
                        color: article.category === 'Market Trends' ? '#0a233b' : article.category === 'Investment' ? '#f66b10' : '#f66b10',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                          color: 'inherit'
                        }
                      }}
                    />
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600, 
                        color: '#0a233b',
                        fontSize: '1.1rem',
                        lineHeight: 1.4,
                        mb: 1.5,
                        height: '3.1rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {article.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        height: '4.5rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {article.description}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 'auto'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            width: 28, 
                            height: 28, 
                            backgroundColor: '#0a233b',
                            fontSize: '0.8rem',
                            mr: 1
                          }}
                        >
                          {article.author.charAt(0)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          {article.author}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {article.readTime}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          mt: 8, 
          textAlign: 'center',
          p: 4,
          backgroundColor: 'rgba(166, 209, 234, 0.1)',
          borderRadius: 2
        }}>
          <Typography variant="h5" sx={{ color: '#0a233b', mb: 2, fontWeight: 600 }}>
            Have a question about property management?
          </Typography>
          <Typography variant="body1" sx={{ color: '#6a6565', mb: 3, maxWidth: '700px', mx: 'auto' }}>
            Our team of experts is ready to help you navigate the complexities of property management and real estate investment.
          </Typography>
          <Button 
            variant="contained" 
            component={Link}
            to="/contact"
            sx={{ 
              backgroundColor: '#f66b10',
              '&:hover': {
                backgroundColor: '#d45500',
              },
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            Contact Us
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CommunityBlog;
